import axios from 'axios';
import { Button } from 'components/buttons';
import { FormTextInput, FormSelect, FormRadio, FormSwitch } from "components/form-fields";
import { ShowHide } from "components/layout";
import { ListingTable } from "components/listing";
import { StatusBadge } from "components/status";
import { NotificationInline, NotificationToaster, AlertConfirm } from "components/notifications";
import { useDirtyData } from 'hooks/useDirtyData';
import { useEventWizard } from 'pages/asset/events/shared/EventWizardContext';
import React, { useEffect, useMemo, useState, useRef } from "react";
import { FleetService } from "services";
import { v4 as uuidv4 } from 'uuid';
import { Tooltip } from 'components/tooltip';
import { DefectSignoffModal } from 'pages/defects/shared';
import { Icon, Intent } from "@blueprintjs/core";
import moment from "moment";
import { EventAddLaterOptions } from 'pages/asset/events/modals/DefaultModels';

const DEFECT_ITEMS_LIMIT = 100;

export function DefectEvent({}) {

    const { 
        event,
        eventType, 
        saving, 
        loadingData, 
        stepsConfig, 
        tempDefectList, 
        setStepsConfig, 
        setStepIsValid, 
        setTempDefectList, 
        setEvent, 
        eventOptions,
        setHasMadeChanges,
        setActiveStep,
        setCanSave
    } = useEventWizard();

    const isMountedRef = useRef(false);

    const isStepDataDirty = useDirtyData({
        defects: event?.defects ?? [],
    }, {
        defects: tempDefectList,
    });

    const maxCommentSize = 1000;

    const config = stepsConfig.DefectEvent;
    const { signoffDefectLater, notSignedOffDefect } = config;

    const [isLoadingDefect, setIsLoadingDefect] = useState(false);
    const [defectType, setDefectType] = useState([]);
    const [showDefectSignoffModal, setShowDefectSignoffModal] = useState(false);
    const [savingSignoff, setSavingSignoff] = useState(false);
    const [localDefect, setLocalDefect] = useState(null);
    const localDefectId = useRef(null);
    const [deletingSignedOffDefect, setDeletingSignedOffDefect] = useState(false);
    const [defectIdToDelete, setDefectIdToDelete] = useState(-1);
    const isDisabled = saving || loadingData;
    // Lock the editable defect fields while a save/refresh is in flight, so an edit made mid-save
    // isn't silently discarded when refreshEventData replaces the list with the server response.
    const fieldsDisabled = isDisabled || isLoadingDefect;

    const refreshEventData = () => {
        FleetService.getEvent(event.eventId).then((getEventTypeResponse) => {
            setEvent(getEventTypeResponse);
            setTempDefectList(getEventTypeResponse.defects || []);
        }).catch(() => {
            NotificationToaster.show(Intent.DANGER, "Failed to refresh modal. Please close and try again.");
        }).finally(() => {
            setIsLoadingDefect(false);
        });
    }

    useEffect(() => {
        setActiveStep("Defect");
    }, []);

    useEffect(() => {
        setCanSave(isStepDataDirty && !isLoadingDefect)
    }, [isStepDataDirty, isLoadingDefect])

    config.onSave = () => {
        let promises = [];

        if (tempDefectList?.length > 0) {
            let defectsToCreate = {
                eventDefects: tempDefectList.filter(d => !d.eventDefectId)
            };
            let defectsToUpdate = {
                eventDefects: tempDefectList.filter(d => d.eventDefectId)
            };
            if (defectsToCreate?.eventDefects?.length > 0) {
                promises.push(FleetService.createEventDefect(event.eventId, defectsToCreate));
            }
            if (defectsToUpdate?.eventDefects?.length > 0) {
                promises.push(FleetService.updateEventDefect(event.eventId, defectsToUpdate));
            }
        }

        if (promises.length === 0) {
            return;
        }

        setIsLoadingDefect(true);

        axios.all(promises).then(axios.spread(() => {
            setHasMadeChanges(true);
            //We'll refresh to rest the dirty flag
            refreshEventData();
            NotificationToaster.show(Intent.SUCCESS, "Defect item updated successfully.");
        }), () => {
            NotificationToaster.show(Intent.DANGER, "Failed to update the defect item. Please try again.");
            setIsLoadingDefect(false);
        });
    }

    const deleteDefectItem = (id) => {
        setIsLoadingDefect(true);

        FleetService.deleteEventDefect(event?.eventId, id)
            .then(() => {
                setHasMadeChanges(true);
                refreshEventData();
                NotificationToaster.show(Intent.SUCCESS, "Defect deleted successfully.");
            })
            .catch(() => {
                NotificationToaster.show(Intent.DANGER, "Failed to delete the defect. Please try again.");
                setIsLoadingDefect(true);
            });
    }

    const isDefectTypeSafetyCritical = (eventDefectTypeId) => {
        if (!eventDefectTypeId || !eventOptions?.eventDefectTypes) {
            return false;
        }

        const matchedDefectType = eventOptions.eventDefectTypes.find(dt => dt.id === eventDefectTypeId);
        return matchedDefectType?.safetyCritical === true;
    };

    const onDefectAdd = (data) => {
        if (!data.eventDefectTypeId) {
            return;
        }

        setTempDefectList(prev => ([
            ...prev,
            {
                tempId: uuidv4(),
                eventDefectTypeId: data.eventDefectTypeId,
                safety: isDefectTypeSafetyCritical(data.eventDefectTypeId)
            },
        ]));
    }

    const onUpdateDefectItem = (id, data) => {
        setTempDefectList(prevState => prevState.map(item => {
            if (item.tempId === id || item.eventDefectId === id) {
                return {
                    ...item,
                    ...data
                };
            }
            return item;
        }));
    };

    const onDeleteDefectItem = (id) => {

        const item = tempDefectList.find(i => i.tempId === id || i.eventDefectId === id);
        const isTempItem = !!item?.tempId;

        if (isTempItem) {
            setTempDefectList(prevState => prevState.filter(i => i.tempId !== id));
        } else if (item.signoffDate) {
            //This has been signed off so let's put up a confirmation box
            setDefectIdToDelete(id);
            setDeletingSignedOffDefect(true);
        } else {
            setTempDefectList(prevState => prevState.filter(i => i.defectTypeId !== id));
            deleteDefectItem(id);
        }
    };

    const onSignoffItem = (id, defect) => {
        setLocalDefect(defect);
    };

    function onRadioFieldChange(item) {
        setStepsConfig(prev => ({
            ...prev,
            DefectEvent: {
                ...prev.DefectEvent,
                signoffDefectLater: item.currentTarget.value === "true"
            }
        }));
    }

    const handleInputChange = (itemId, data) => {
        if (itemId === -1) {
            onDefectAdd(data);
        } else {
            onUpdateDefectItem(itemId, data);
        }
    }

    const defectEventHeaders = useMemo(() => {

        const headers = ["Defect Type", "Im number", "Comment", "Safety"];

        if (tempDefectList?.length > 0 && !isDisabled) {
            headers.push("Actions");
        }

        return headers;
    }, [tempDefectList, eventType, isDisabled]);

    function onDeletSignedOffDefect() {
        setTempDefectList(prevState => prevState.filter(i => i.defectTypeId !== defectIdToDelete));
        deleteDefectItem(defectIdToDelete);
        setDeletingSignedOffDefect(false);
        setDefectIdToDelete(-1);
    }

    function onDeleteCancel() {
        setDefectIdToDelete(-1);
        setDeletingSignedOffDefect(false);
    }

    function onCloseSignoffModal() {
        setShowDefectSignoffModal(false);
        localDefectId.current = null;
        setLocalDefect(null);
    }

    function signOffDefect(defectSignOff) {
        setSavingSignoff(true);

        FleetService.signOffDefect(event.eventId, defectSignOff.eventDefectId, defectSignOff).then(
            () => {
                NotificationToaster.show(Intent.SUCCESS, "Defect signed off successfully.");
                refreshEventData();
                setShowDefectSignoffModal(false);
            })
            .catch((error) => {
                NotificationToaster.show(Intent.DANGER, error);
            })
            .finally(() => {
                setSavingSignoff(false);
                onCloseSignoffModal();
            });
    }

    const defectEventsListingData = useMemo(() => {

        const list = [
            ...tempDefectList,
        ];

        if (!isDisabled && list.length < DEFECT_ITEMS_LIMIT) {
            list.push({
                tempId: -1,
            });
        }

        return list.map((ec) => {
            const itemId = ec.eventDefectId ?? ec.tempId;
            let imNumber = ec.defectIMNumber;
            let matchedDefectType = null;
            if (ec?.eventDefectTypeId && eventOptions?.eventDefectTypes) {
                matchedDefectType = eventOptions.eventDefectTypes.find(dt => dt.id === ec.eventDefectTypeId);
            }
            if (!imNumber && matchedDefectType) {
                imNumber = matchedDefectType.imNumber ?? "";
            }

            // A defect whose type is flagged safety-critical is always treated as a safety defect.
            // The toggle is locked on in that case; otherwise it reflects the defect's own safety flag and stays editable.
            const isSafetyCriticalType = matchedDefectType?.safetyCritical === true;
            const isSafety = isSafetyCriticalType || ec.safety === true;

            const inputs = [
                <div key={`defect-type-select-${itemId}`} className='inline-flex-center'>
                    <FormSelect
                        items={defectType}
                        selectedValue={ec.eventDefectTypeId}
                        onItemSelect={(s) => handleInputChange(itemId, { eventDefectTypeId: s.id, safety: s.safetyCritical === true ? true : (ec.safety === true) })}
                        placeholder="Please select to create a new defect"
                        dynamicWidth={true}
                        disabled={fieldsDisabled || ec.signoffDate || signoffDefectLater}
                    ></FormSelect>
                </div>,
                <div key={`defect-im-number-${itemId}`} className='inline-flex-center'>
                    {imNumber}
                </div>,
                <div key={`defect-comment-${itemId}-key`} className='inline-flex-center'>
                    <FormTextInput
                        disabled={fieldsDisabled || itemId === -1 || signoffDefectLater}
                        onChange={(value) => handleInputChange(itemId, {defectComment: value.target.value })}
                        value={ec.defectComment ?? ""}
                        id={`defect-comment-${itemId}`}
                        maxLength={maxCommentSize}
                        wide={true}
                    />
                </div>,
                <div key={`defect-safety-${itemId}-key`} className='inline-flex-center'>
                    <ShowHide
                        evaluator={itemId !== -1}
                        show={(
                            <span className='inline-items'>
                                <FormSwitch
                                    id={`defect-safety-${itemId}`}
                                    checked={isSafety}
                                    disabled={fieldsDisabled || isSafetyCriticalType || ec.signoffDate || signoffDefectLater}
                                    onChange={(value) => handleInputChange(itemId, { safety: value.target.checked })}
                                />
                                <ShowHide
                                    evaluator={isSafety}
                                    show={(
                                        <span className="nowrap">
                                            <StatusBadge
                                                text="Safety"
                                                iconName="warning-sign"
                                                intent={Intent.DANGER}
                                                showTooltip={true}
                                                tooltipContent="This is a safety-critical defect."
                                            />
                                        </span>
                                    )}
                                />
                            </span>
                        )}
                    />
                </div>
            ];

            let actions = [];

            if (itemId !== -1 && !isDisabled) {
                actions.push(
                    <div key={`defect-delete-${itemId}`}>
                        <Tooltip key={`delete-tt-${itemId}`} content={`Delete`}>
                            <Button 
                                iconOnly 
                                icon="trash" 
                                minimal 
                                large={false} 
                                onClick={() => onDeleteDefectItem(itemId)}
                                disabled={signoffDefectLater}
                            />
                        </Tooltip>
                    </div>
                );

                if (!ec.tempId) {
                    if (!ec.signoffDate) {
                        actions.push(
                            <div key={`defect-signoff-${itemId}`}>
                                <Tooltip key={`signoff-tt-${itemId}`} content={`Signoff`}>
                                    <Button 
                                        iconOnly 
                                        icon="tick-circle" 
                                        minimal 
                                        large={false} 
                                        onClick={() => 
                                        onSignoffItem(itemId, ec)}
                                        disabled={signoffDefectLater}
                                    />
                                </Tooltip>
                            </div>
                        );
                    } else if (ec.rectified) {
                        let signoffDetails = `Signed off by ${ec.signoffUser} on ${moment(ec.signoffDate).format("DD MMM YYYY")}`;
                        actions.push(
                            <div key={`defect-signoff-${itemId}`} style={{ paddingLeft: '7px' }}>
                                <Tooltip key={`signoff-tt-${itemId}`} content={signoffDetails}>
                                    <Icon icon="tick-circle" intent={Intent.SUCCESS} />
                                </Tooltip>
                            </div>
                        );
                    } else if (ec.monitored) {
                        let signoffDetails = `Set to monitored by ${ec.signoffUser} on ${moment(ec.signoffDate).format("DD MMM YYYY")}`;
                        actions.push(
                            <div key={`defect-signoff-${itemId}`}>
                                <Tooltip key={`signoff-tt-${itemId}`} content={signoffDetails}>
                                    <Button 
                                        iconOnly 
                                        icon="search" 
                                        intent={Intent.WARNING} 
                                        minimal 
                                        large={false}
                                        onClick={() => onSignoffItem(itemId, ec)}
                                        disabled={signoffDefectLater}
                                    />
                                </Tooltip>
                            </div>
                        );
                    } else {
                        let signoffDetails = `Set to not rectified by ${ec.signoffUser} on ${moment(ec.signoffDate).format("DD MMM YYYY")}`;
                        actions.push(
                            <div key={`defect-signoff-${itemId}`}>
                                <Tooltip key={`signoff-tt-${itemId}`} content={signoffDetails}>
                                    <Button 
                                        iconOnly 
                                        icon="delete" 
                                        intent={Intent.DANGER} 
                                        minimal 
                                        large={false} 
                                        onClick={() => onSignoffItem(itemId, ec)}
                                        disabled={signoffDefectLater}
                                    />
                                </Tooltip>
                            </div>
                        );
                    }
                    
                }
            }

            inputs.push(
                <div className='inline-flex-center'>
                    {actions}
                </div>
            );

            return inputs;
        });
    }, [defectType, tempDefectList, eventType, isDisabled, isLoadingDefect, signoffDefectLater, eventOptions]);

    useEffect(() => {
        if (!eventOptions?.eventDefectTypes) {
            return;
        }

        setDefectType(eventOptions.eventDefectTypes.map(dt => ({
            ...dt,
            eventDefectTypeId: dt.defectTypeId
        })));
    }, [eventOptions]);

    useEffect(() => {
        if (!localDefect?.eventDefectId || localDefectId.current === localDefect.eventDefectId) {
            return;
        }

        localDefectId.current = localDefect.eventDefectId;
        setShowDefectSignoffModal(true);
    }, [localDefect]);

    useEffect(() => {
        if (isMountedRef.current || tempDefectList.length > 0) {
            return;
        }

        const eventDefects = event?.defects || [];
        eventDefects.length && setTempDefectList(eventDefects);
        isMountedRef.current = true;
    }, [event]);

    useEffect(() => {
        setStepIsValid(!isStepDataDirty && (!config.defectIsRequired || signoffDefectLater || event.defects.length > 0) && (!config.notSignedOffDefect || signoffDefectLater));
    }, [isStepDataDirty, tempDefectList, config.defectIsRequired, signoffDefectLater, event, config.notSignedOffDefect]);

    return (
        <>
            <div>
                <h2>Event Defect</h2>

                <NotificationInline
                    id="notification-defect-helper"
                    loading={loadingData}
                    show={eventType?.defectHelperText && eventType.defectHelperText.length > 0}
                    text={eventType?.defectHelperText}
                    allowClose={false} 
                />

                <NotificationInline
                    id="notification-defect-unsaved-changes"
                    intent={Intent.WARNING}
                    loadingData={loadingData || isLoadingDefect}
                    show={isStepDataDirty && !isLoadingDefect || !signoffDefectLater && notSignedOffDefect}
                    text={
                        <div>
                            <p><ShowHide evaluator={isStepDataDirty} show={<>There are unsaved changes in this step.</>}/> <ShowHide evaluator={notSignedOffDefect} show={<>Some defects are not signed off yet.</>}/></p>
                        </div>
                    }
                    allowClose={false} 
                />

                <ShowHide
                    evaluator={!isStepDataDirty && (config.notSignedOffDefect || (config.defectIsRequired && !config.hasDefects))}
                    show={
                        <FormRadio
                            disabled={saving}
                            options={EventAddLaterOptions}
                            onChange={(item) => onRadioFieldChange(item)}
                            headingText={config.hasDefects ? "When do you want to signoff defect(s)?" : "Add defect(s) later"}
                            selectedValue={signoffDefectLater}
                            loading={loadingData}
                            id="input-signoff-defects-later"
                        />
                    }
                />

                <ShowHide
                    evaluator={!signoffDefectLater}
                    show={
                        <ListingTable
                            id="defect-event-listing"
                            headers={defectEventHeaders}
                            data={defectEventsListingData}
                            totalRecordCount={defectEventsListingData.length}
                            pageable={false}
                            loadingData={loadingData}
                        />
                    }
                />
            </div>

            <DefectSignoffModal
                title="Sign Off Defect"
                isOpen={showDefectSignoffModal}
                loading={loadingData}
                defect={localDefect}
                onClose={onCloseSignoffModal}
                signOffDefect={signOffDefect}
                saving={savingSignoff}
                UserAccessActions={["FMS:Edit"]}
            />

            <AlertConfirm
                title="Delete Defect"
                isOpen={deletingSignedOffDefect}
                onConfirm={onDeletSignedOffDefect}
                onCancel={onDeleteCancel}
                confirmButtonIntent="primary"
                confirmButtonText="delete"
            >
                <p>Are you sure you want to delete this signed off defect?</p>
            </AlertConfirm>
        </>
    );
}

DefectEvent.propTypes = {
};